<template>
  <div class="receipt-wrap">
    <div v-if="r" class="actions no-print">
      <v-btn small color="primary" @click="print">
        <v-icon left small>mdi-printer</v-icon>{{ use_language.reprint }}
      </v-btn>
    </div>
    <div v-if="r" class="receipt">
      <div class="center bold big">{{ r.shopName }}</div>
      <div v-if="r.shopAddress" class="center small">{{ r.shopAddress }}</div>
      <div v-if="r.shopPhone" class="center small">{{ use_language.call }}: {{ r.shopPhone }}</div>
      <div class="center bold big mt-2">{{ use_language.receipt }}</div>
      <div class="dashed"></div>

      <div class="row small">
        <span>{{ use_language.no_2 }}: {{ r.orderId }}</span>
      </div>
      <div class="row small">
        <span>{{ use_language.date }}: {{ formatDate(r.createdAt) }}</span>
      </div>
      <div class="row small">
        <span>{{ use_language.cashier }}: {{ r.cashierId || '-' }}</span>
      </div>
      <div class="row small">
        <span>{{ use_language.customer }}: {{ r.customer ? r.customer.name : 'Walk-in' }}</span>
      </div>
      <div v-if="r.customer && r.customer.phone" class="row small">
        <span>{{ use_language.call }}: {{ r.customer.phone }}</span>
      </div>

      <div class="dashed"></div>

      <table class="items">
        <thead>
          <tr>
            <th class="left" colspan="2">{{ use_language.item_unit }}</th>
            <th class="right">{{ use_language.sum_total }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(it, i) in r.items">
            <tr :key="'name-' + i">
              <td class="no">{{ i + 1 }}.</td>
              <td colspan="2" class="item-name">{{ it.name }}</td>
            </tr>
            <tr :key="'qty-' + i">
              <td class="indent"></td>
              <td class="left">{{ it.quantity }} x {{ formatPrice(it.salePrice) }}</td>
              <td class="right">{{ formatPrice(lineGross(it)) }}</td>
            </tr>
            <tr v-if="it.discount" :key="'disc-' + i">
              <td class="indent"></td>
              <td class="left">{{ use_language.discount }}</td>
              <td class="right">- {{ formatPrice(it.discount) }}</td>
            </tr>
          </template>
        </tbody>
      </table>

      <div class="dashed"></div>

      <div class="row">
        <span>{{ use_language.total }}</span>
        <span class="right-cell">{{ formatPrice(r.subtotal) }}</span>
      </div>
      <div v-if="r.itemDiscount" class="row">
        <span>{{ use_language.product_discount }}</span>
        <span class="right-cell">- {{ formatPrice(r.itemDiscount) }}</span>
      </div>
      <div v-if="r.orderDiscount" class="row">
        <span>{{ use_language.bill_discount }}</span>
        <span class="right-cell">- {{ formatPrice(r.orderDiscount) }}</span>
      </div>
      <div class="row bold total-row">
        <span>{{ use_language.net_amount }}</span>
        <span class="right-cell">{{ formatPrice(r.grandTotal) }} ฿</span>
      </div>

      <div class="dashed"></div>

      <div v-if="r.paymentLabel" class="row small">
        <span>{{ use_language.paid_by }}</span>
        <span class="right-cell">{{ r.paymentLabel }}</span>
      </div>
      <div v-if="r.paymentReference" class="row small">
        <span>{{ use_language.reference_no_label }}</span>
        <span class="right-cell">{{ r.paymentReference }}</span>
      </div>
      <div v-if="r.receivedAmount != null" class="row small">
        <span>{{ use_language.receive_money }}</span>
        <span class="right-cell">{{ formatPrice(r.receivedAmount) }}</span>
      </div>
      <div v-if="r.changeAmount != null" class="row bold">
        <span>{{ use_language.change_money }}</span>
        <span class="right-cell">{{ formatPrice(r.changeAmount) }}</span>
      </div>

      <div v-if="r.paymentLabel" class="dashed"></div>

      <div class="center mt-0">
        <barcode :value="String(r.orderId)" :height="40" :font-size="12" :display-value="true" />
      </div>
      <div class="center small mt-0">{{ use_language.thank_you_for_service }}</div>
    </div>
    <div v-else class="pa-4 text-center grey--text">
      {{ use_language.receipt_not_found }}
    </div>
  </div>
</template>

<script>
import VueBarcode from 'vue-barcode'
import { get_languages } from '@/website/global_function'

export default {
  name: 'PosPrintReceipt',
  components: { barcode: VueBarcode },
  data: () => ({ r: null, use_language: {} }),
  async created () {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"))
    try {
      const raw = localStorage.getItem('pos_receipt_payload')
      this.r = raw ? JSON.parse(raw) : null
    } catch (e) {
      this.r = null
    }
  },
  mounted () {
    if (this.r) {
      setTimeout(this.print, 250)
    }
  },
  methods: {
    get_languages,
    print () {
      window.print()
    },
    lineGross (it) {
      return Number(it.salePrice || 0) * Number(it.quantity || 0)
    },
    formatPrice (v) {
      return Number(v || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate (iso) {
      try {
        const d = new Date(iso)
        const pad = n => String(n).padStart(2, '0')
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
          ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
.receipt-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f0f0f0;
  min-height: 100vh;
  padding: 12px 0;
}
.receipt {
  width: 80mm;
  background: white;
  padding: 6mm 4mm;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #000;
  line-height: 1.4;
}
.center { text-align: center; }
.actions {
  width: 80mm;
  margin-bottom: 8px;
  text-align: center;
}
.bold { font-weight: bold; }
.small { font-size: 11px; }
.big { font-size: 13px; }
.mt-1 { margin-top: 2px; }
.mt-2 { margin-top: 6px; }
.dashed {
  border-top: 1px dashed #000;
  margin: 4px 0;
}
.row {
  display: flex;
  justify-content: space-between;
}
.right-cell { text-align: right; }
.total-row { font-size: 13px; margin-top: 4px; }
.items {
  width: 100%;
  border-collapse: collapse;
}
.items th, .items td {
  padding: 1px 0;
  vertical-align: top;
  font-size: 11px;
}
.items th.left, .items td.left { text-align: left; }
.items th.right, .items td.right { text-align: right; }
.items td.item-name {
  font-weight: bold;
  font-size: 10px;
  line-height: 1.25;
  padding-top: 3px !important;
}
.items td.no {
  width: 16px;
  font-weight: bold;
  padding-top: 3px !important;
}

@media print {
  .no-print {
    display: none !important;
  }
  .receipt-wrap {
    background: white;
    padding: 0;
    min-height: auto;
  }
  .receipt {
    width: 80mm;
    padding: 2mm;
  }
  @page {
    size: 80mm auto;
    margin: 0;
  }
}
</style>
